import AddIcon from '@mui/icons-material/Add';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import StarIcon from '@mui/icons-material/Star';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  Avatar,
  Box,
  Button,
  Chip,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { listBrands, listCategories, listTags, searchProducts } from '../../api/business/endpoints';
import type { ProductSortKey, ProductType, ProductVisibility, RecordStatus } from '../../api/business/types';
import { usePermissions } from '../../auth/usePermissions';
import { flattenCategories } from '../../components/categoryTree';
import { CompanySelector } from '../../components/CompanySelector';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { formatDate, formatMoney, formatNumber, formatPercent } from '../../components/format';
import { PageHeader } from '../../components/PageHeader';
import { VAT_RATES } from '../../components/prices';
import { PRODUCT_TYPES } from '../../components/productTypes';
import { ServerPagination } from '../../components/ServerPagination';
import { SortableCell } from '../../components/SortableCell';
import { StatusChip } from '../../components/StatusChip';
import { TableFilters } from '../../components/TableFilters';
import { TableSkeletonRows } from '../../components/TableSkeletonRows';
import { useDebouncedValue } from '../../components/useDebouncedValue';
import { useSelectedCompany } from '../../components/useSelectedCompany';
import { useSort } from '../../components/useSort';
import { useLanguage } from '../../i18n/LanguageContext';

import { ProductDialog } from './ProductDialog';

/**
 * One company's catalogue, server-paginated. Variants, batches and on-hand are columns, not sortable: they are
 * counts and sums across other tables the API does not order by (Conventions/08 §4). The price is what the
 * product sells for now — sorted by the regular price, the column the API orders by. A category filter takes
 * the categories under it too; the category, brand and tag filters can come in the address (R0030 WP-06). The
 * main category and the brand are columns, not sortable: they are names in other tables.
 */
export const ProductsPage = () => {
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const permissions = usePermissions();
  const selection = useSelectedCompany('products.read');
  const company = selection.company;
  const [term, setTerm] = useState('');
  const [status, setStatus] = useState<RecordStatus | ''>('ACTIVE');
  const [visibility, setVisibility] = useState<ProductVisibility | ''>('');
  const [vatRate, setVatRate] = useState<number | ''>('');
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [productType, setProductType] = useState<ProductType | ''>('');
  const [searchParams] = useSearchParams();
  const [categoryId, setCategoryId] = useState(searchParams.get('categoryId') ?? '');
  const [brandId, setBrandId] = useState(searchParams.get('brandId') ?? '');
  const [tagId, setTagId] = useState(searchParams.get('tagId') ?? '');
  const [page, setPage] = useState(0);
  const [isCreating, setIsCreating] = useState(false);
  const sort = useSort<ProductSortKey>('name', 'asc', () => setPage(0));
  const debouncedTerm = useDebouncedValue(term);
  const canManage =
    company !== null && permissions.writable && permissions.can('products.manage', company.id);

  const query = useQuery({
    queryKey: [
      'products',
      company?.id,
      debouncedTerm,
      status,
      visibility,
      vatRate,
      featuredOnly,
      productType,
      categoryId,
      brandId,
      tagId,
      page,
      sort.key,
      sort.direction,
    ],
    enabled: company !== null,
    queryFn: () =>
      searchProducts(company?.id ?? '', {
        term: debouncedTerm,
        status: status || undefined,
        visibility: visibility || undefined,
        vatRate: vatRate === '' ? undefined : vatRate,
        featured: featuredOnly ? 'true' : undefined,
        categoryId: categoryId || undefined,
        brandId: brandId || undefined,
        tagId: tagId || undefined,
        type: productType || undefined,
        page: page + 1,
        sort: sort.key,
        direction: sort.direction,
      }),
    placeholderData: keepPreviousData,
  });

  const rows = query.data?.items ?? [];
  const categories = useQuery({
    queryKey: ['categories', company?.id],
    enabled: company !== null,
    queryFn: () => listCategories(company?.id ?? ''),
  });
  const brands = useQuery({
    queryKey: ['brands', company?.id],
    enabled: company !== null,
    queryFn: () => listBrands(company?.id ?? ''),
  });
  const tags = useQuery({
    queryKey: ['tags', company?.id],
    enabled: company !== null,
    queryFn: () => listTags(company?.id ?? ''),
  });

  if (!selection.isLoading && company === null) {
    return (
      <>
        <PageHeader title={t('products.title')} />
        <ErrorNotice error={selection.error} onRetry={selection.retry} />
        <Paper variant="outlined">
          <EmptyState
            title={t('products.noCompany')}
            detail={t('products.noCompanyDetail')}
            action={<Button onClick={() => navigate('/companies')}>{t('nav.companies')}</Button>}
          />
        </Paper>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t('products.title')}
        subtitle={t('products.subtitle')}
        action={
          <Stack direction="row" spacing={1.5} alignItems="center">
            <CompanySelector
              companies={selection.companies}
              value={company}
              onChange={(companyId) => {
                selection.select(companyId);
                setCategoryId('');
                setBrandId('');
                setTagId('');
                setPage(0);
              }}
            />
            {canManage ? (
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setIsCreating(true)}>
                {t('products.create')}
              </Button>
            ) : null}
          </Stack>
        }
      />
      <ErrorNotice error={query.error ?? selection.error} onRetry={() => void query.refetch()} />
      <Paper variant="outlined">
        <TableFilters>
          <TextField
            size="small"
            label={t('common.search')}
            placeholder={t('products.searchHint')}
            value={term}
            onChange={(event) => {
              setTerm(event.target.value);
              setPage(0);
            }}
          />
          <TextField
            select
            size="small"
            label={t('field.status')}
            value={status}
            onChange={(event) => {
              setStatus(event.target.value as RecordStatus | '');
              setPage(0);
            }}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            <MenuItem value="ACTIVE">{t('status.ACTIVE')}</MenuItem>
            <MenuItem value="ARCHIVED">{t('status.ARCHIVED')}</MenuItem>
          </TextField>
          <TextField
            select
            size="small"
            label={t('field.visibility')}
            value={visibility}
            onChange={(event) => {
              setVisibility(event.target.value as ProductVisibility | '');
              setPage(0);
            }}
            sx={{ minWidth: 170 }}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            {(['EVERYWHERE', 'CATALOGUE', 'SEARCH', 'HIDDEN'] as Array<ProductVisibility>).map((one) => (
              <MenuItem key={one} value={one}>
                {t(`visibility.${one}`)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label={t('products.vat')}
            value={vatRate}
            onChange={(event) => {
              setVatRate(event.target.value === '' ? '' : Number(event.target.value));
              setPage(0);
            }}
            sx={{ minWidth: 110 }}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            {VAT_RATES.map((rate) => (
              <MenuItem key={rate} value={rate}>
                {formatPercent(rate, language)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label={t('products.category')}
            value={categoryId}
            onChange={(event) => {
              setCategoryId(event.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            {flattenCategories(categories.data ?? []).map((one) => (
              <MenuItem
                key={one.category.id}
                value={one.category.id}
                sx={{ pl: 2 + (one.category.depth - 1) * 2 }}
              >
                {one.category.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label={t('products.brand')}
            value={brandId}
            onChange={(event) => {
              setBrandId(event.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            {(brands.data ?? []).map((brand) => (
              <MenuItem key={brand.id} value={brand.id}>
                {brand.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label={t('products.tag')}
            value={tagId}
            onChange={(event) => {
              setTagId(event.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 130 }}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            {(tags.data ?? []).map((tag) => (
              <MenuItem key={tag.id} value={tag.id}>
                {tag.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label={t('field.productType')}
            value={productType}
            onChange={(event) => {
              setProductType(event.target.value as ProductType | '');
              setPage(0);
            }}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            {PRODUCT_TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {t(`productType.${type}`)}
              </MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={featuredOnly}
                onChange={(event) => {
                  setFeaturedOnly(event.target.checked);
                  setPage(0);
                }}
              />
            }
            label={t('products.featuredOnly')}
          />
        </TableFilters>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <SortableCell sort={sort} column="name">
                  {t('field.productName')}
                </SortableCell>
                <TableCell>{t('products.mainCategory')}</TableCell>
                <TableCell>{t('products.brand')}</TableCell>
                <SortableCell sort={sort} column="ean">
                  {t('field.ean')}
                </SortableCell>
                <SortableCell sort={sort} column="sku">
                  {t('field.sku')}
                </SortableCell>
                <SortableCell sort={sort} column="defaultPriceMinor" align="right">
                  {t('products.price')}
                </SortableCell>
                <SortableCell sort={sort} column="vatRateBasisPoints" align="right">
                  {t('products.vat')}
                </SortableCell>
                <TableCell align="right">{t('products.variants')}</TableCell>
                <TableCell align="right">{t('products.batches')}</TableCell>
                <TableCell align="right">{t('products.onHand')}</TableCell>
                <TableCell>{t('field.status')}</TableCell>
                <SortableCell sort={sort} column="createdAt">
                  {t('field.createdAt')}
                </SortableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {query.isLoading || selection.isLoading ? (
                <TableSkeletonRows columns={12} />
              ) : (
                rows.map((product) => (
                  <TableRow
                    key={product.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/products/${product.companyId}/${product.id}`)}
                  >
                    <TableCell sx={{ fontWeight: 560 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Avatar
                          variant="rounded"
                          src={product.mainImage?.urls.thumb.webp}
                          alt={product.mainImage ? product.name : t('gallery.noPhoto')}
                          sx={{
                            width: 36,
                            height: 36,
                            mr: 0.75,
                            bgcolor: 'action.hover',
                            color: 'text.disabled',
                          }}
                        >
                          <ImageOutlinedIcon fontSize="small" />
                        </Avatar>
                        {product.name}
                        {product.type === 'PACK' ? (
                          <Chip size="small" variant="outlined" color="info" label={t('productType.PACK')} />
                        ) : null}
                        {product.featured ? (
                          <Tooltip title={t('products.featured')}>
                            <StarIcon
                              fontSize="inherit"
                              color="warning"
                              aria-label={t('products.featured')}
                            />
                          </Tooltip>
                        ) : null}
                        {product.visibility !== 'EVERYWHERE' ? (
                          <Tooltip
                            title={t('products.hiddenHint', {
                              visibility: t(`visibility.${product.visibility}`),
                            })}
                          >
                            <VisibilityOffIcon
                              fontSize="inherit"
                              color="action"
                              aria-label={t(`visibility.${product.visibility}`)}
                            />
                          </Tooltip>
                        ) : null}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {product.mainCategory ? (
                        <Tooltip title={product.mainCategory.path.join(' › ')}>
                          <span>{product.mainCategory.name}</span>
                        </Tooltip>
                      ) : (
                        t('common.none')
                      )}
                    </TableCell>
                    <TableCell>{product.brand?.name ?? t('common.none')}</TableCell>
                    <TableCell sx={{ fontFamily: 'ui-monospace, monospace' }}>
                      {product.ean ?? t('common.none')}
                    </TableCell>
                    <TableCell>{product.sku ?? t('common.none')}</TableCell>
                    <TableCell align="right">
                      {formatMoney(product.effectivePriceMinor, company?.currency ?? 'EUR', language)}
                      {product.unitPriceMinor !== null && product.unitLabel !== null ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {t('legal.unitPriceValue', {
                            price: formatMoney(product.unitPriceMinor, company?.currency ?? 'EUR', language),
                            unit: t(`unitSymbol.${product.unitLabel}`),
                          })}
                        </Typography>
                      ) : null}
                      {product.saleActive ? (
                        <Box
                          sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', alignItems: 'center' }}
                        >
                          <Chip
                            size="small"
                            color="secondary"
                            variant="outlined"
                            label={t('products.onSale')}
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ textDecoration: 'line-through' }}
                          >
                            {formatMoney(product.defaultPriceMinor, company?.currency ?? 'EUR', language)}
                          </Typography>
                        </Box>
                      ) : null}
                    </TableCell>
                    <TableCell align="right">{formatPercent(product.vatRateBasisPoints, language)}</TableCell>
                    <TableCell align="right">
                      {product.variantCount === 0
                        ? t('common.none')
                        : formatNumber(product.variantCount, language)}
                    </TableCell>
                    <TableCell align="right">{formatNumber(product.batchCount, language)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {product.availableQuantity === null
                        ? t('products.alwaysAvailable')
                        : formatNumber(
                            product.type === 'PACK' ? product.availableQuantity : product.totalOnHand,
                            language,
                          )}
                      {product.type === 'PACK' ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', fontWeight: 400 }}
                        >
                          {t('products.packHint')}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <StatusChip status={product.status} />
                    </TableCell>
                    <TableCell>{formatDate(product.createdAt, language)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {!query.isLoading && company !== null && rows.length === 0 ? (
          <EmptyState
            title={t('products.empty')}
            detail={canManage ? t('products.emptyManage') : t('products.emptyRead')}
            action={
              canManage ? (
                <Button startIcon={<AddIcon />} onClick={() => setIsCreating(true)}>
                  {t('products.create')}
                </Button>
              ) : undefined
            }
          />
        ) : null}
        <ServerPagination
          page={page}
          pageSize={query.data?.pageSize ?? 25}
          total={query.data?.total ?? 0}
          onPageChange={setPage}
        />
      </Paper>
      {isCreating && company ? (
        <ProductDialog
          companyId={company.id}
          currency={company.currency}
          product={null}
          onClose={() => setIsCreating(false)}
          onSaved={(saved) => navigate(`/products/${company.id}/${saved.id}`)}
        />
      ) : null}
    </>
  );
};
